import { isProblem } from './problem'

/**
 * КЛЮЧ ИДЕМПОТЕНТНОСТИ ПРИНАДЛЕЖИТ ДЕЙСТВИЮ, А НЕ ВЫЗОВУ ФУНКЦИИ.
 *
 * Значение заголовка `Idempotency-Key`, который контракт требует у операций с НЕОБРАТИМЫМ ВНЕШНИМ
 * ЭФФЕКТОМ. Ручек ТРИ: создание брони, отправка сообщения гостю и запись платежа — про последнюю
 * контракт пишет, что повтор «записал бы ВТОРОЙ платёж живыми деньгами»
 * (`openapi/paths/bookings.yaml`, `recordBookingPayment`). Контракт пишет прямо: «…чтобы двойной
 * клик или повтор запроса после сбоя сети не выполнили действие ещё раз», — и помнит под ключом
 * исход: выполненное повтор получает ответом, известное невыполнение ключ освобождает, неизвестный
 * исход держит его сутки (`openapi/components/parameters.yaml#/IdempotencyKey`).
 *
 * ⚠️ ОТСЮДА ФОРМА API, И ОНА НЕ ОБСУЖДАЕТСЯ: КЛЮЧ ПРИХОДИТ СНАРУЖИ. Спрятать его внутрь обёртки
 * (`mutate(body)` → `crypto.randomUUID()` внутри) выглядит заботой о чистоте API и уничтожает саму
 * защиту. Клиент не может отличить «сервер не получил запрос» от «получил, создал бронь, а ответ
 * потерялся по дороге», поэтому повтор обязан прийти с ТЕМ ЖЕ ключом. Обёртка, минтящая ключ внутри,
 * на «Повторить» сминтит НОВЫЙ — и гость получит вторую бронь или второе сообщение.
 *
 * Ключ рождается там, где живёт САМО ДЕЙСТВИЕ, и уезжает в мутацию параметром: у очереди отправки
 * (`features/message-send`) — на «Отправить», и дальше живёт в попытках сообщения до успеха и уходит
 * с каждым «Повторить»; у формы создания брони — на ПЕРВОЕ нажатие «Создать бронь» и
 * переиспользуется, пока бронь не создана.
 *
 * ⚠️ ПРАВИЛО ЛОМАЕТСЯ В ОБЕ СТОРОНЫ — ПРОВЕРЯЙ ОБЕ:
 *
 *   старый ключ там, где нужен новый  →  действие МОЛЧА не выполнилось
 *   новый ключ там, где нужен старый  →  действие выполнилось ДВАЖДЫ
 *
 * Первая ошибка тише второй: на старый ключ сервер вернёт результат ПРОШЛОГО вызова, и экран
 * отрапортует об успехе действия, которого не было.
 *
 * ПОЧЕМУ В `shared/api`, А НЕ В СУЩНОСТИ: это значение HTTP-заголовка, объявленного в контракте один
 * раз на все ручки, — того же рода, что `newRequestId` (`X-Request-Id`) рядом; его требуют три
 * ручки из двух доменов. Разъехаться могла бы не функция в одну строку, а ЭТОТ ТЕКСТ.
 */
export function newIdempotencyKey(): string {
  return crypto.randomUUID()
}

/**
 * Ответ самого ключа (`openapi/components/responses.yaml`, `IdempotencyKeyTaken`,
 * `IdempotencyKeyReused`): первый запрос ещё выполняется (409) или выполнен, а ответ не собран
 * (5xx), исход его неизвестен и серверу, или по ключу выполнено другое. Их `title` и `detail` —
 * слова для менеджера, и статус их не различает: неизвестный исход приходит и 5xx, и 409.
 */
export type KeyAnswer = 'busy' | 'unknown' | 'reused'

const KEY_ANSWERS: Readonly<Record<string, KeyAnswer>> = {
  'https://prima.ru/errors/idempotency-key-busy': 'busy',
  'https://prima.ru/errors/idempotency-outcome-unknown': 'unknown',
  'https://prima.ru/errors/idempotency-key-reused': 'reused',
}

export function keyAnswer(error: unknown): KeyAnswer | null {
  return isProblem(error) ? (KEY_ANSWERS[error.type] ?? null) : null
}

/**
 * Ответит ли тот же ключ тем же: исход первого нажатия неизвестен и серверу, или по ключу выполнено
 * другое. Повтор им бесполезен — выход в чтении (лента, список) и новом окне, уже с новым ключом.
 */
export function sameKeySameAnswer(error: unknown): boolean {
  const answer = keyAnswer(error)
  return answer === 'unknown' || answer === 'reused'
}

/**
 * Что делать по ответу ключа, когда сервер не написал сам: общий совет велел бы обновить страницу, а
 * с ней пропал бы ключ формы. `busy` на 409 — первое нажатие ещё выполняется, на 5xx — выполнено, а
 * ответ не собран (`openapi/components/parameters.yaml#/IdempotencyKey`).
 */
export function keyAdvice(error: unknown): string | null {
  const answer = keyAnswer(error)
  if (!isProblem(error) || !answer) return null
  if (answer === 'unknown') return 'Исход первого нажатия неизвестен: проверьте результат, прежде чем повторять.'
  if (answer === 'reused') return 'Первое нажатие уже выполнено с прежними данными — повтор его не меняет.'
  return error.status >= 500
    ? 'Первое нажатие выполнено, а ответ не собрался: повторите — второй раз оно не выполнится.'
    : 'Первое нажатие ещё выполняется: повторите чуть позже — второй раз оно не выполнится.'
}

/**
 * Могло ли действие под ключом всё-таки состояться: сбой сети, 5xx или ответ ключа. Тогда то, чем
 * исход проверяют (лента платежей, список броней), надо перечитать — иначе человек увидит прежнее,
 * откроет новое окно с новым ключом и выполнит действие второй раз.
 */
export function mayHaveActed(error: unknown): boolean {
  return !isProblem(error) || error.status >= 500 || keyAnswer(error) !== null
}
